import type { DriverEvents, MediaCall, RegistrationState, TelephonyDriver } from './driver';
import { assertDtmf } from './driver';
import type { TelephonyConfig } from '../lib/types';

/**
 * Simulator driver for the sandbox.
 *
 * It implements the full interface and reports the same state transitions a
 * real trunk does, but never touches WebRTC — there is no microphone prompt, no
 * ICE, no audio. Calls in the demo are therefore silent by design: everything
 * the console does around a call is real, and only the audio itself is absent.
 *
 * That is the honest way to demo a CAD. Faking audio would make the sandbox
 * behave in ways the production system cannot.
 */
export class MockDriver implements TelephonyDriver {
  readonly name = 'mock' as const;
  readonly hasMedia = false;

  private events: DriverEvents | null = null;
  private calls = new Map<string, MediaCall>();
  private state: RegistrationState = 'idle';

  async start(_config: TelephonyConfig, events: DriverEvents): Promise<void> {
    this.events = events;
    this.setState('connecting');
    // A short delay so the console shows the same "registering…" moment it will
    // show against a real trunk, rather than snapping to registered instantly.
    await new Promise((resolve) => setTimeout(resolve, 250));
    this.setState('registered', 'Simulated trunk — no audio path');
  }

  async stop(): Promise<void> {
    this.calls.clear();
    this.setState('idle');
    this.events = null;
  }

  private setState(state: RegistrationState, detail?: string): void {
    this.state = state;
    this.events?.onRegistration(state, detail);
  }

  get registrationState(): RegistrationState {
    return this.state;
  }

  async answer(sipCallId?: string | null): Promise<void> {
    const id = sipCallId ?? [...this.calls.keys()][0];
    if (!id) return;
    const call = this.calls.get(id) ?? {
      sipCallId: id,
      direction: 'inbound' as const,
      remoteIdentity: 'simulated',
      displayName: null,
      establishedAt: null,
    };
    call.establishedAt = Date.now();
    this.calls.set(id, call);
    this.events?.onEstablished(call);
  }

  async hangup(sipCallId?: string | null): Promise<void> {
    const id = sipCallId ?? [...this.calls.keys()][0];
    if (!id) return;
    this.calls.delete(id);
    this.events?.onEnded(id, 'local_hangup');
  }

  async hold(_sipCallId: string | null, _held: boolean): Promise<void> {
    // Hold is a CAD-record state in the simulator; there is no media to divert.
  }

  async mute(_muted: boolean): Promise<void> {
    // No microphone is ever opened.
  }

  async sendDtmf(_sipCallId: string | null, digits: string): Promise<void> {
    assertDtmf(digits);
  }

  async dial(destination: string): Promise<string> {
    const sipCallId = `mock-out-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const call: MediaCall = {
      sipCallId,
      direction: 'outbound',
      remoteIdentity: destination,
      displayName: null,
      establishedAt: null,
    };
    this.calls.set(sipCallId, call);
    // The simulated far end picks up after a plausible ring.
    setTimeout(() => {
      if (!this.calls.has(sipCallId)) return;
      call.establishedAt = Date.now();
      this.events?.onEstablished(call);
    }, 1200);
    return sipCallId;
  }

  async transfer(sipCallId: string | null, _target: string): Promise<void> {
    if (sipCallId) this.calls.delete(sipCallId);
  }

  streams(): MediaStream[] {
    return [];
  }

  /** Lets the sandbox mark a server-generated call as ringing on this handset. */
  simulateIncoming(sipCallId: string, remoteIdentity: string, displayName: string | null): void {
    const call: MediaCall = {
      sipCallId,
      direction: 'inbound',
      remoteIdentity,
      displayName,
      establishedAt: null,
    };
    this.calls.set(sipCallId, call);
    this.events?.onIncoming(call);
  }
}
